import { LogOut, Menu, Moon, Sun, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import logotipoClaro from '@/assets/marca/logotipo-fundo-claro.png';
import logotipoEscuro from '@/assets/marca/logotipo-fundo-escuro.png';
import { BotaoIcone } from '@/componentes/BotaoIcone';
import { Dica } from '@/componentes/Dica';
import { Button } from '@/componentes/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/componentes/ui/dropdown-menu';
import { AMBIENTE, APP_NOME, mostrarSeloDeAmbiente } from '@/lib/ambiente';
import { useAuth } from '@/lib/auth';
import { useTema } from '@/lib/tema';

/** O selo só aparece fora de produção — em prod o ambiente é o esperado e o selo seria ruído. */
function SeloAmbiente() {
  if (!mostrarSeloDeAmbiente()) return null;
  return (
    <Dica texto="Ambiente de testes: nada do que se faz aqui vale para a operação real">
      <span
        tabIndex={0}
        className="cursor-help rounded-md border border-border bg-secondary px-2 py-0.5 text-xs font-medium tracking-wide text-secondary-foreground uppercase focus-visible:outline-2 focus-visible:outline-ring"
      >
        {AMBIENTE}
      </span>
    </Dica>
  );
}

/** Iniciais do nome, para o avatar textual. Primeira e última palavra — "Ingrid Almeida" → "IA". */
function iniciaisDoNome(nome: string | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0][0];
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (primeira + ultima).toUpperCase();
}

export function Topbar({ aoAbrirMenu }: { aoAbrirMenu: () => void }) {
  const { tema, alternarTema } = useTema();
  const { usuario, encerrarSessao } = useAuth();
  const navegar = useNavigate();

  const sair = async () => {
    await encerrarSessao();
    navegar('/entrar', { replace: true });
  };

  return (
    <header className="flex h-16 items-center justify-between gap-2 border-b border-border bg-card px-2 md:px-6">
      <div className="flex items-center gap-2 md:gap-3">
        <BotaoIcone
          rotulo="Abrir menu"
          dica="Mostra os módulos da plataforma"
          icone={<Menu aria-hidden />}
          onClick={aoAbrirMenu}
          className="md:hidden"
        />

        {/*
         * Duas artes, uma por fundo: a dourada é desenhada para grafite e sobre marfim daria 1,70:1;
         * a preta é a versão de fundo claro. Trocamos pelo tema, nunca forçamos uma sobre a outra
         * (docs/MARCA.md §5). A troca é por CSS (`dark:`) e não por `useTema()` para o logo certo já
         * vir no primeiro quadro, sem piscar a arte errada.
         *
         * Só UMA das duas é anunciada: `alt` na visível e `alt=""` na escondida, senão o leitor de
         * tela leria "Tríplice Capital" duas vezes.
         */}
        <Dica texto="Volta para a página inicial">
          <Link to="/" className="flex items-center gap-2" aria-label={`${APP_NOME} — início`}>
            <img src={logotipoClaro} alt={APP_NOME} className="h-9 w-auto dark:hidden" />
            <img src={logotipoEscuro} alt="" aria-hidden className="hidden h-9 w-auto dark:block" />
          </Link>
        </Dica>
        <SeloAmbiente />
      </div>

      <div className="flex items-center gap-1">
        <BotaoIcone
          rotulo={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
          dica={
            tema === 'escuro'
              ? 'Troca para o fundo claro; a escolha fica guardada neste navegador'
              : 'Troca para o fundo escuro; a escolha fica guardada neste navegador'
          }
          icone={tema === 'escuro' ? <Sun aria-hidden /> : <Moon aria-hidden />}
          onClick={alternarTema}
        />

        <DropdownMenu>
          <Dica texto="Seu perfil e a saída da plataforma">
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-11 gap-2 px-2" aria-label="Menu da conta">
                <span
                  aria-hidden
                  className="flex size-8 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-foreground"
                >
                  {iniciaisDoNome(usuario?.nome)}
                </span>
                <span className="hidden max-w-40 truncate text-sm font-medium sm:inline">
                  {usuario?.nome}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </Dica>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="space-y-0.5">
              <p className="truncate text-sm font-medium">{usuario?.nome}</p>
              <p className="truncate text-xs font-normal text-muted-foreground">{usuario?.email}</p>
              {usuario?.papel ? (
                <p className="text-xs font-normal text-muted-foreground">{usuario.papel}</p>
              ) : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <Dica texto="Seus dados, sua senha e suas permissões" lado="left">
              <DropdownMenuItem asChild className="gap-2">
                <Link to="/perfil">
                  <User aria-hidden />
                  Meu perfil
                </Link>
              </DropdownMenuItem>
            </Dica>
            <DropdownMenuSeparator />
            <Dica texto="Encerra a sessão neste navegador" lado="left">
              <DropdownMenuItem onSelect={() => void sair()} className="gap-2">
                <LogOut aria-hidden />
                Sair
              </DropdownMenuItem>
            </Dica>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
